/**
 * «Перерисовать картинку» и «Нарисовать картинку» — ОДНА форма с двумя заголовками.
 *
 * Двух диалогов здесь нет и не должно быть: маршрут (`POST …/request-changes`), тело
 * (`{prompt, targets:['visual']}`) и последствие (пост уходит на переделку и возвращается
 * новой версией) у них совпадают полностью. Различие ровно одно — есть ли что перерисовывать,
 * — и оно выражено заголовком, а не вторым компонентом.
 *
 * `targets: ['visual']` СОБЛЮДАЕТСЯ БУКВАЛЬНО (`services/agent-writer/src/rework.ts`): текст
 * остаётся побайтово прежним, а вердикт картинки выносится по ОПИСАНИЮ ЧЕЛОВЕКА — либо, если
 * описания нет, по фактам самого поста.
 *
 * ОПИСАНИЕ НЕОБЯЗАТЕЛЬНО НА «НАРИСОВАТЬ» (правка 10, §8.1 `docs/CHANGES-2026-09-08.md`).
 * Раньше кнопка была заблокирована на пустом поле, и человек, который просто хочет картинку
 * по посту, обязан был пересказать модели свой же пост. Теперь пустое поле уезжает
 * литералом `VISUAL_AUTO_PROMPT` из контрактов.
 *
 * ПОЧЕМУ ЛИТЕРАЛ, А НЕ ПУСТАЯ СТРОКА. Ни 400 на маршруте, ни `CHECK (length(prompt) BETWEEN
 * 1 AND 2000)` в базе не ослабляются — и не должны: `prompt` переделки это текст, который
 * читает модель, и «пустой текст» ей не задача. Сентинел с префиксом `auto:` — это то, КАК
 * выражено «я не сказал, что рисовать»: по нему решатель визуала включает ТРЕТЬЕ состояние
 * входа (`humanAsked`), где обоснование фактов остаётся `required` (человек фактов не
 * приносил, ослаблять проверку происхождения нечем), а опровергающий проход пропускается
 * (человек попросил явно). Заменишь литерал пустой строкой — и на карточку смогут попасть
 * числа, которых в посте нет, то есть прямое нарушение ВИЗ-6.
 *
 * НА «ПЕРЕРИСОВАТЬ» ПОЛЕ ОСТАЁТСЯ ОБЯЗАТЕЛЬНЫМ, и это не забытая ветка. Пустая перерисовка
 * означает «попробуй ещё раз без новой информации»: модель почти наверняка вернёт тот же
 * вердикт, версии не будет, и черновик застрянет «на переделке», выйти откуда можно только
 * приходом v+1.
 *
 * ОБЛОЖКА — ТА ЖЕ ФОРМА С ДРУГИМ ВОПРОСОМ (`kind: 'poster'`, docs/phase-2/10-posters.md). Маршрут,
 * тело и последствие прежние; меняются три вещи: строка просьбы едет с приставкой обложки
 * (`posterRedrawPrompt`), комментарий необязателен и на «перерисовать» (дизайнер выбирает
 * формат и фон заново, и пустая просьба — законное «попробуй иначе»), а пояснения говорят про
 * крючок и фон, а не про макеты с данными.
 *
 * Своего модального окна нет — `components/Modal.tsx`: Escape, возврат фокуса и
 * `aria-labelledby` в копиях расходятся молча.
 */

import { posterRedrawPrompt, postLabel, VISUAL_AUTO_PROMPT } from '@smm/contracts/schemas';
import { type JSX, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type { RenderModel, RequestChangesResponse } from '../../api/types.ts';
import { isAgentsOff } from '../../brand.tsx';
import { Modal } from '../../components/Modal.tsx';

const PROMPT_MAX = 2000;

export interface GenerateVisualDialogProps {
  readonly model: RenderModel;
  /** `true` — картинка или предложение уже есть: заголовок про перерисовку. */
  readonly redraw: boolean;
  /** Что рисуем: инфографику по фактам поста (умолчание) или обложку. */
  readonly kind?: 'visual' | 'poster';
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

export function GenerateVisualDialog({
  model,
  redraw,
  kind = 'visual',
  onChanged,
  onClose,
}: GenerateVisualDialogProps): JSX.Element {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const poster = kind === 'poster';
  const trimmed = prompt.trim();
  const ready = (trimmed.length > 0 || !redraw || poster) && trimmed.length <= PROMPT_MAX;
  const wire = poster ? posterRedrawPrompt(trimmed) : trimmed === '' ? VISUAL_AUTO_PROMPT : trimmed;

  const submit = (): void => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    post<RequestChangesResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${String(model.version_no)}/request-changes`,
      { prompt: wire, targets: ['visual'] },
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        // Отказ бренда без сервиса генерации — тоже 409, но черновик не менялся: текст
        // сервера называет причину (docs/multi-brand.md §8).
        if (e instanceof ApiRequestError && e.status === 409 && !isAgentsOff(e)) {
          setError('Состояние черновика изменилось — обновите экран.');
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (poster) {
    return (
      <Modal
        title={redraw ? 'Перерисовать обложку' : 'Нарисовать обложку'}
        subtitle={`${postLabel(model.post_no)} · v${String(model.version_no)}`}
        onClose={onClose}
      >
        <div className="stack" style={{ gap: 8 }}>
          <label className="lbl" htmlFor="poster-prompt" style={{ margin: 0 }}>
            Что изменить — необязательно: тон крючка, его формат, фон
          </label>
          <textarea
            id="poster-prompt"
            className="inp"
            rows={4}
            maxLength={PROMPT_MAX}
            value={prompt}
            placeholder="Можно оставить пустым. Или: строже, без вопроса; фон темнее."
            onChange={(e) => {
              setPrompt(e.target.value);
            }}
          />
          <div className="lmeter">
            <span className="cnt num">
              {[...trimmed].length} / {PROMPT_MAX}
            </span>
            <span className="unit">символов</span>
          </div>
        </div>

        <div className="notice">
          Пост уйдёт на переделку и вернётся новой версией. Текст не меняется: сервис заново читает
          пост и выбирает формат крючка, сам крючок и фон. Дата в углу и метка канала остаются
          прежними; фоны соседних постов сервис не берёт.
        </div>

        {error === null ? null : (
          <div className="val val-err">
            <span>{error}</span>
          </div>
        )}

        <div className="stack" style={{ gap: 10 }}>
          <button
            type="button"
            className={ready && !busy ? 'btn btn-pri btn-w' : 'btn btn-w btn-dis'}
            disabled={!ready || busy}
            onClick={submit}
          >
            Отправить на переделку
          </button>
          <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
            Отмена
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={redraw ? 'Перерисовать картинку' : 'Нарисовать картинку'}
      subtitle={`${postLabel(model.post_no)} · v${String(model.version_no)}`}
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 8 }}>
        <label className="lbl" htmlFor="visual-prompt" style={{ margin: 0 }}>
          {redraw
            ? 'Что должно быть на картинке — обязательно: без нового описания вердикт повторится'
            : 'Что должно быть на картинке — необязательно: без описания сервис возьмёт факты из поста'}
        </label>
        <textarea
          id="visual-prompt"
          className="inp"
          rows={5}
          maxLength={PROMPT_MAX}
          value={prompt}
          placeholder={
            redraw
              ? 'Например: сравнить агента покупателя и агента магазина по трём признакам — задача, кому отвечает, что видит.'
              : 'Можно оставить пустым. Или уточнить: сравнить агента покупателя и агента магазина по трём признакам.'
          }
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
        />
        <div className="lmeter">
          <span className="cnt num">
            {[...trimmed].length} / {PROMPT_MAX}
          </span>
          <span className="unit">символов</span>
        </div>
      </div>

      <div className="notice">
        Пост уйдёт на переделку и вернётся новой версией. Текст при этом не меняется — сервис
        перерисовывает только картинку. Ход переделки виден в шапке, слева от кнопки запуска.
      </div>

      {redraw ? (
        <div className="notice">
          Описание обязательно: пустая перерисовка — это «попробуй ещё раз без новой информации», и
          сервис почти наверняка вернёт тот же вердикт. Новой версии тогда не будет, а пост
          останется «на переделке».
        </div>
      ) : null}

      <div className="notice">
        {trimmed === '' && !redraw
          ? 'Без описания сервис возьмёт фактическую часть поста — числа, имена, порядок, сравнения — и разложит её по подходящему макету: сравнение, порядок шагов, цифра с базой сравнения. Если ни один макет не заполняется без выдумки, сервис откажет и назовёт, чего в посте не хватает.'
          : 'Описание должно ложиться на один из трёх макетов: сравнение, порядок шагов, цифра с базой сравнения.'}{' '}
        Отказ виден в полосе хода в шапке и приходит алертом на «Мониторинг»: новой версии в этом
        случае не будет, пост останется «на переделке», и вывести его оттуда сможет только приход
        версии v+1.
      </div>

      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        <button
          type="button"
          className={ready && !busy ? 'btn btn-pri btn-w' : 'btn btn-w btn-dis'}
          disabled={!ready || busy}
          onClick={submit}
        >
          Отправить на переделку
        </button>
        <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
          Отмена
        </button>
      </div>
    </Modal>
  );
}
